import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import LinearProgress from "@mui/material/LinearProgress";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";

import { Draft, Run, Team } from "../types";
import {
  RunStatusChip,
  AgentStatusChip,
  DraftStatusChip,
} from "../components/StatusChip";
import StagePipeline from "../components/StagePipeline";
import { formatDateTime, relativeTime } from "../utils/format";

interface DashboardProps {
  team: Team;
  runs: Run[];
  drafts: Draft[];
  onTriggerRun: () => void;
  onNavigate: (view: string) => void;
}

const StatCard: React.FC<{
  label: string;
  value: string;
  icon: React.ReactElement;
  tint: string;
}> = ({ label, value, icon, tint }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar
          variant="rounded"
          sx={{ bgcolor: `${tint}1a`, color: tint, width: 48, height: 48 }}
        >
          {icon}
        </Avatar>
        <Box>
          <Typography variant="h4">{value}</Typography>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const Dashboard: React.FC<DashboardProps> = ({
  team,
  runs,
  drafts,
  onTriggerRun,
  onNavigate,
}) => {
  const lastRun = runs[0];
  const pendingDrafts = drafts.filter((d) => d.status === "PENDING_REVIEW");
  const avgEffort =
    drafts.length > 0
      ? Math.round(
          (drafts.reduce((s, d) => s + d.editEffort, 0) / drafts.length) * 100
        )
      : 0;

  return (
    <Box>
      {/* Hero */}
      <Card
        sx={{
          mb: 3,
          color: "#fff",
          background:
            "linear-gradient(135deg, #3b3f8f 0%, #5a3fa0 55%, #13b5b1 140%)",
          border: "none",
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <AgentStatusChip status={team.agentStatus} />
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  {team.schedule}
                </Typography>
              </Stack>
              <Typography variant="h3" sx={{ mb: 0.5 }}>
                {team.name}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 560 }}>
                {team.description}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack
                spacing={1.5}
                alignItems={{ xs: "flex-start", md: "flex-end" }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  startIcon={<PlayArrowRoundedIcon />}
                  onClick={onTriggerRun}
                  sx={{
                    bgcolor: "#fff",
                    color: "primary.main",
                    "&:hover": { bgcolor: "#eef0ff" },
                  }}
                >
                  Trigger manual run
                </Button>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  Last run {relativeTime(team.lastRunAt)}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Stat cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Pending reviews"
            value={String(pendingDrafts.length)}
            icon={<DescriptionRoundedIcon />}
            tint="#d98a00"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Metric profiles"
            value={String(team.metricProfiles)}
            icon={<InsightsRoundedIcon />}
            tint="#3b3f8f"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Recipients"
            value={String(team.recipients)}
            icon={<GroupsRoundedIcon />}
            tint="#13b5b1"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Avg edit effort"
            value={`${avgEffort}%`}
            icon={<BoltRoundedIcon />}
            tint="#1f9d57"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        {/* Latest run */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">Latest run</Typography>
                <Button size="small" onClick={() => onNavigate("runs")}>
                  View all runs
                </Button>
              </Stack>
              {lastRun ? (
                <Box>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ mb: 3, flexWrap: "wrap", gap: 1 }}
                  >
                    <RunStatusChip status={lastRun.status} />
                    <Typography variant="body2" color="text.secondary">
                      {lastRun.id} · {lastRun.trigger} ·{" "}
                      {formatDateTime(lastRun.startedAt)}
                    </Typography>
                  </Stack>
                  <StagePipeline stages={lastRun.stages} />
                </Box>
              ) : (
                <Typography color="text.secondary">No runs yet.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Self-improving loop */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                Self-improving loop
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Lower edit effort means drafts need fewer human corrections over
                time.
              </Typography>
              <Stack spacing={2}>
                {drafts.map((d) => (
                  <Box key={d.id}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: 0.5 }}
                    >
                      <Typography variant="body2">
                        Week of {d.weekOf}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {Math.round(d.editEffort * 100)}% edited
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.round(d.editEffort * 100)}
                      sx={{ height: 8, borderRadius: 5 }}
                      color={
                        d.editEffort < 0.1
                          ? "success"
                          : d.editEffort < 0.25
                          ? "warning"
                          : "error"
                      }
                    />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Drafts needing attention */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">Drafts awaiting review</Typography>
                <Button size="small" onClick={() => onNavigate("drafts")}>
                  Open review queue
                </Button>
              </Stack>
              <Stack spacing={1.5}>
                {drafts.map((d) => (
                  <Box
                    key={d.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      cursor: "pointer",
                      transition: "background-color .15s",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                    onClick={() => onNavigate("drafts")}
                  >
                    <Box>
                      <Typography variant="subtitle2">
                        Week of {d.weekOf}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ maxWidth: 720 }}
                      >
                        {d.summary}
                      </Typography>
                    </Box>
                    <DraftStatusChip status={d.status} />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
